import React, { PropsWithChildren } from 'react'
import {
  arrow as arrowMiddleware,
  autoUpdate,
  Coords,
  flip as flipMiddleware,
  FloatingPortal,
  limitShift,
  offset as offsetMiddleware,
  shift as shiftMiddleware,
  useFloating,
  useInteractions,
  useRole,
} from '@floating-ui/react'
import { PopoverPlacement } from '../helperTypes'
import { CommonProps } from '../helperTypes'

type Props = PropsWithChildren<{
  /**
   * hide arrow if false
   * @default true
   */
  arrow?: boolean
  /** custom arrow classname */
  arrowClassName?: string
  /** custom arrow size */
  arrowSize?: number
  /** disable automated aria roles */
  disableRoles?: boolean
  /** fallback placements for floating-ui flip modifier */
  fallbackPlacements?: Array<PopoverPlacement>
  /**
   * optional horizontal and vertical offset
   * @default [0, 8]
   */
  offset?: [number, number]
  /** controlled open state */
  open?: boolean
  /**
   * wanted placement
   * @default bottom
   */
  placement?: PopoverPlacement
  /** reference node to pass to floating-ui */
  referenceNode?: HTMLElement | null
  /** to pass the role as tooltip */
  role?: string
}>

export type PopperProps = Props & CommonProps

export type PopperHandle = {
  update: (() => void) | null
}

// computes arrow element css positioning
// depending on floating-ui computed placement and middleware data
const getArrowStyles = (
  placement: string,
  arrowData: Partial<Coords>,
  shiftData: Partial<Coords>,
  offsetData: Partial<Coords>,
  arrowHeight: number = 8,
) => {
  const side = placement.split('-')[0]
  const arrowHalfSize = arrowHeight / 2
  // FIXME could be better to use transform instead of top/left/right/bottom
  // see https://floating-ui.com/docs/misc#subpixel-and-accelerated-positioning
  const styles: any = { [side]: `calc(100% - ${arrowHalfSize}px)` }
  if (side === 'left') {
    if (arrowData.x) styles.left = arrowData.x
    else styles.right = 0
  } else if (side === 'bottom') {
    if (arrowData.y) styles.bottom = arrowData.y
    else styles.top = -arrowHalfSize
  }
  if (side === 'left' || side === 'right') {
    styles.top = Math.max(0, (arrowData.y || 0) - (shiftData.y || 0) - (offsetData.y || 0))
  } else {
    styles.left = Math.max(0, (arrowData.x || 0) - (shiftData.x || 0) - (offsetData.x || 0) - arrowHalfSize / 2)
  }
  return styles
}

const Popper = React.forwardRef<PopperHandle, PopperProps>(
  (
    {
      referenceNode,
      className,
      style: userStyles,
      arrowClassName,
      arrowSize = 8,
      children,
      disableRoles = false,
      placement = 'bottom',
      fallbackPlacements,
      offset = [0, 8],
      arrow = true,
      open = false,
      ...rest
    },
    ref,
  ) => {
    const arrowRef = React.useRef<HTMLDivElement | null>(null)

    const {
      context,
      floatingStyles,
      middlewareData: { arrow: arrowData, shift: shiftData, offset: offsetData },
      refs,
      update,
    } = useFloating({
      elements: {
        reference: referenceNode,
      },
      placement,
      open,
      whileElementsMounted: autoUpdate,
      middleware: [
        arrowMiddleware({ element: arrowRef }),
        offsetMiddleware({
          crossAxis: offset[0],
          mainAxis: offset[1],
        }),
        flipMiddleware({ fallbackPlacements }),
        shiftMiddleware({ crossAxis: true, limiter: limitShift() }),
      ],
    })
    React.useImperativeHandle(ref, () => ({ update }))

    const role = useRole(context, { enabled: !disableRoles })
    const { getReferenceProps, getFloatingProps } = useInteractions([role])

    // FIXME maybe expose a compound component API (eg. Popover > PopoverTrigger, PopoverContent)
    // Parent would provide context to "PopoverTrigger" element
    // instead of manipulating dom attributes through a "ref prop"
    const refProps: any = getReferenceProps()
    React.useEffect(() => {
      if (!referenceNode) return
      const attNames = Object.keys(refProps)
      attNames.forEach((att) => {
        if (refProps[att] === undefined) {
          referenceNode.removeAttribute(att)
        } else {
          referenceNode.setAttribute(att, refProps[att])
        }
      })
    }, [referenceNode, refProps])

    const arrowStyles = React.useMemo(() => {
      if (!open || !arrowData || !shiftData || !offsetData) return undefined
      return getArrowStyles(context.placement, arrowData, shiftData, offsetData, arrowSize)
    }, [arrowData, arrowSize, context.placement, offsetData, open, shiftData])

    if (!open) return null
    return (
      <FloatingPortal>
        <div
          className={className}
          ref={refs.setFloating}
          style={{ ...floatingStyles, ...userStyles }}
          data-popper-placement={context.placement}
          {...getFloatingProps()}
          {...rest}
        >
          {children}
          {arrow && <div className={arrowClassName} ref={arrowRef} style={arrowStyles} />}
        </div>
      </FloatingPortal>
    )
  },
)

export default Popper
